import is from '@sindresorhus/is';
import { useMemo } from 'react';

import { MAIN_TILE_ID } from '~/ui/domain/tiling-engine/constants';

import { type ChatContext, useCanUseAgentChat, useRegisterChatContext } from '../../chat';
import { useLearningOpportunitiesPermissions } from '../api';
import { LEARNING_OPPORTUNITY_TILE_SLOT_KEY } from '../constants';
import type { SidepanelMode } from '../form-state';

const LEARNING_OPPORTUNITIES_CHAT_CONTEXT: ChatContext = { kind: 'learning-opportunities' };

const useCanManageLearningOpportunitiesThroughAgentChat = (): boolean => {
    const canUseAgentChat = useCanUseAgentChat();
    const { canUserSeeLearningOpportunities: canManageLearningOpportunities } = useLearningOpportunitiesPermissions();

    return canUseAgentChat && canManageLearningOpportunities;
};

export const toLearningOpportunityChatContext = (
    opportunityId: string | undefined,
    mode: SidepanelMode,
): ChatContext | undefined =>
    mode === 'create' || !is.nonEmptyString(opportunityId)
        ? undefined
        : { kind: 'learning-opportunity', mode, opportunityId };

export const useRegisterLearningOpportunitiesChatContext = (): void => {
    const canUseChat = useCanManageLearningOpportunitiesThroughAgentChat();

    useRegisterChatContext(MAIN_TILE_ID, canUseChat ? LEARNING_OPPORTUNITIES_CHAT_CONTEXT : undefined);
};

export const useRegisterLearningOpportunityChatContext = (
    opportunityId: string | undefined,
    mode: SidepanelMode,
): void => {
    const canUseChat = useCanManageLearningOpportunitiesThroughAgentChat();
    const chatContext = useMemo(
        () => (canUseChat ? toLearningOpportunityChatContext(opportunityId, mode) : undefined),
        [canUseChat, mode, opportunityId],
    );

    useRegisterChatContext(LEARNING_OPPORTUNITY_TILE_SLOT_KEY, chatContext);
};
